import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward, CheckCircleOutline, Close, ExpandMore, LocalOfferOutlined } from '@mui/icons-material';
import { API_CALLS_HINT } from '../constants/apiUsageCopy';
import { ApiCallsHint } from './ApiCallsHint';
import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { tokens } from '../theme/tokens';
import { useIsMobile } from '../hooks/useIsMobile';

interface BillingPlan {
  id: string;
  name: string;
  description: string;
  whoItsFor?: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  limits: {
    maxUsers: number;
    maxApiCalls: number;
    maxDataSync: number;
    productSyncCap?: boolean;
    maxIntegrations: number;
  };
  isPopular?: boolean;
  stripePriceId?: string;
  setupFee?: number;
  setupFeeCustom?: boolean;
}

interface UpgradePlansModalProps {
  open: boolean;
  onClose: () => void;
  currentPlanId: string;
  organizationId?: string;
  /** Active base setup-fee waiver from /billing/my-subscription, if any */
  setupFeeWaiver?: { campaign?: string | null; expiresAt?: string | null } | null;
}

// The Canada stack's plans are priced in CAD; say so rather than show a bare "$".
const formatPrice = (price: number, currency = 'USD') =>
  price === 0
    ? 'Free'
    : `$${price.toLocaleString('en-US')}${currency && currency.toUpperCase() !== 'USD' ? ` ${currency.toUpperCase()}` : ''}`;
const formatLimit = (value: number) => (value === -1 ? 'Unlimited' : value.toLocaleString('en-US'));

const limitRows = (plan: BillingPlan): Array<{ label: string; value: string; hint?: string }> => [
  { label: 'API calls/month', value: formatLimit(plan.limits.maxApiCalls), hint: API_CALLS_HINT },
  {
    label: 'Products synced',
    value: plan.limits.productSyncCap === true && plan.limits.maxDataSync !== -1 ? formatLimit(plan.limits.maxDataSync) : 'Unlimited',
  },
  { label: 'Integrations', value: formatLimit(plan.limits.maxIntegrations) },
  { label: 'Team members', value: formatLimit(plan.limits.maxUsers) },
];

const isFreeTier = (plan: BillingPlan) => plan.price === 0;

export const UpgradePlansModal: React.FC<UpgradePlansModalProps> = ({
  open,
  onClose,
  currentPlanId,
  organizationId,
  setupFeeWaiver = null,
}) => {
  const isMobile = useIsMobile();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [promoOpen, setPromoOpen] = useState(false);

  const { data: plans, isLoading } = useQuery<BillingPlan[]>({
    queryKey: ['billing-plans'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/plans', { headers });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const result = await response.json();
      return result.data || [];
    },
    enabled: open,
  });

  const selectedIsCustomSetup = !!selectedPlan && plans?.find(p => p.id === selectedPlan)?.setupFeeCustom === true;

  const handleClose = () => {
    setSelectedPlan(null);
    setPromoOpen(false);
    setCouponCode('');
    setError(null);
    onClose();
  };

  const handleUpgrade = async () => {
    if (!selectedPlan || !organizationId) {
      setError('Please select a plan to continue');
      return;
    }
    setUpgrading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/subscriptions', {
        method: 'POST',
        headers,
        body: JSON.stringify({ organizationId, planId: selectedPlan, couponCode: couponCode.trim() || undefined }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error?.message || errorData?.message || 'Failed to upgrade plan');
      }
      const result = await response.json();
      if (result.data?.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upgrade plan');
    } finally {
      setUpgrading(false);
    }
  };

  const sorted = [...(plans || [])].sort((a, b) => a.price - b.price);
  const freePlan = sorted.find(isFreeTier);
  const paidPlans = sorted.filter(p => !isFreeTier(p));
  const currentIsFree = !!freePlan && freePlan.id === currentPlanId;

  const selectButtonSx = (state: 'current' | 'selected' | 'idle') => ({
    height: 38,
    borderRadius: '9px',
    fontSize: 13.5,
    fontWeight: 700,
    textTransform: 'none' as const,
    boxShadow: 'none',
    ...(state === 'current'
      ? { border: '1px solid', borderColor: tokens.line.default, bgcolor: tokens.surface.sunken, color: tokens.ink.faint }
      : state === 'selected'
        ? { bgcolor: tokens.accent.main, color: tokens.ink.inverse, '&:hover': { bgcolor: tokens.accent.hover, boxShadow: 'none' } }
        : { border: '1px solid', borderColor: tokens.accent.tintStrong, bgcolor: tokens.surface.card, color: tokens.accent.hover, '&:hover': { bgcolor: tokens.accent.tint } }),
    '&.Mui-disabled': { bgcolor: tokens.surface.sunken, color: tokens.ink.faint, borderColor: tokens.line.default },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxWidth: 1160,
          maxHeight: isMobile ? '100%' : '90vh',
          borderRadius: isMobile ? 0 : '16px',
          boxShadow: tokens.shadow.overlay,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          m: isMobile ? 0 : 3,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 3.25, pt: 2.75, pb: 1.75, flex: '0 0 auto' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700 }}>Choose Your Plan</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>
            Select the plan that best fits your needs. All plans include a 14-day free trial.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: tokens.ink.faint }}>
          <Close sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 3.25, pt: 0.75, pb: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {freePlan && !currentIsFree && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2.5,
                  flexWrap: 'wrap',
                  px: 2.5,
                  py: 2,
                  border: '1px solid',
                  borderColor: selectedPlan === freePlan.id ? tokens.accent.main : tokens.line.default,
                  borderRadius: '13px',
                  mb: 1.75,
                }}
              >
                <Box sx={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{freePlan.name}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{freePlan.description}</Typography>
                </Box>
                <Typography sx={{ flex: '0 0 auto', fontSize: 18, fontWeight: 800 }}>Free</Typography>
                <Box sx={{ flex: '2 1 320px', minWidth: 0, display: 'flex', gap: 2.25, flexWrap: 'wrap' }}>
                  {limitRows(freePlan).map(row => (
                    <Typography key={row.label} sx={{ fontSize: 12, color: tokens.ink.muted }}>
                      {row.label}:{' '}
                      <Box component="span" sx={{ fontWeight: 700, color: tokens.ink.primary }}>
                        {row.value}
                      </Box>
                    </Typography>
                  ))}
                </Box>
                <Button
                  onClick={() => setSelectedPlan(freePlan.id)}
                  sx={{ ...selectButtonSx(selectedPlan === freePlan.id ? 'selected' : 'idle'), height: 36, px: 1.9, flex: '0 0 auto' }}
                >
                  {selectedPlan === freePlan.id ? 'Selected' : 'Select Plan'}
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(230px, 1fr))' }, gap: 1.75 }}>
              {paidPlans.map(plan => {
                const isCurrent = plan.id === currentPlanId;
                const isSelected = selectedPlan === plan.id;
                const isPopular = !!plan.isPopular && !isCurrent;
                const bannerColor = isCurrent ? tokens.signal.positive.solid : tokens.accent.main;
                const hasBanner = isCurrent || isPopular;
                const borderColor = isCurrent ? tokens.signal.positive.solid : isPopular || isSelected ? tokens.accent.main : tokens.line.default;
                const hasSetup = typeof plan.setupFee === 'number' && plan.setupFee > 0;
                const customSetup = plan.setupFeeCustom === true;
                return (
                  <Box
                    key={plan.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      border: `${hasBanner || isSelected ? 1.5 : 1}px solid`,
                      borderColor,
                      borderRadius: '13px',
                      overflow: 'hidden',
                      bgcolor: tokens.surface.card,
                    }}
                  >
                    {hasBanner && (
                      <Box
                        sx={{
                          px: 1.75,
                          py: 0.75,
                          bgcolor: bannerColor,
                          color: tokens.ink.inverse,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          textAlign: 'center',
                        }}
                      >
                        {isCurrent ? 'Current Plan' : 'Most Popular'}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 2.25, pt: 2.25, pb: 1.75, flex: '1 1 auto' }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{plan.name}</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}>
                        <Typography sx={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.01em' }}>
                          {formatPrice(plan.price, plan.currency)}
                          <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: tokens.ink.muted }}>
                            {plan.interval === 'yearly' ? '/yr' : '/mo'}
                          </Box>
                        </Typography>
                        {customSetup && (
                          <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>Custom setup, quoted at onboarding</Typography>
                        )}
                        {hasSetup && !customSetup && (
                          <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>
                            {setupFeeWaiver ? (
                              <>
                                <Box component="span" sx={{ textDecoration: 'line-through' }}>
                                  {formatPrice(plan.setupFee as number, plan.currency)}
                                </Box>{' '}
                                setup fee waived
                              </>
                            ) : (
                              <>+ {formatPrice(plan.setupFee as number, plan.currency)} one-time setup</>
                            )}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{plan.description}</Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                          p: 1.5,
                          bgcolor: tokens.surface.sunken,
                          border: '1px solid',
                          borderColor: tokens.line.hairline,
                          borderRadius: '9px',
                          mt: 0.25,
                        }}
                      >
                        {limitRows(plan).map(row => (
                          <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              {row.label}
                              {row.hint && <ApiCallsHint />}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{row.value}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                        {plan.features.map((feature, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <CheckCircleOutline sx={{ fontSize: 15, color: tokens.signal.positive.solid, mt: '1px', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12.5, color: tokens.ink.body, lineHeight: 1.4 }}>{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ px: 2.25, pb: 2.25 }}>
                      <Button
                        fullWidth
                        disabled={isCurrent}
                        onClick={() => !isCurrent && setSelectedPlan(plan.id)}
                        sx={selectButtonSx(isCurrent ? 'current' : isSelected ? 'selected' : 'idle')}
                      >
                        {isCurrent ? 'Current Plan' : isSelected ? 'Selected' : 'Select Plan'}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>

      {selectedIsCustomSetup && (
        <Box
          sx={{
            flex: '0 0 auto',
            px: 3.25,
            py: 1.25,
            borderTop: '1px solid',
            borderColor: tokens.line.hairline,
            bgcolor: tokens.surface.card,
            fontSize: 12.5,
            color: tokens.ink.bodyAlt,
          }}
        >
          This plan's one-time setup fee is quoted for your store separately. Checkout collects the subscription only, and
          we will follow up with the setup quote before onboarding starts.
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          flexWrap: 'wrap',
          px: 3.25,
          py: 1.75,
          borderTop: '1px solid',
          borderColor: tokens.line.hairline,
          bgcolor: tokens.surface.sunken,
        }}
      >
        <Button
          onClick={() => setPromoOpen(open => !open)}
          startIcon={<LocalOfferOutlined sx={{ fontSize: 14 }} />}
          endIcon={<ExpandMore sx={{ fontSize: 14, color: tokens.ink.faint, transform: promoOpen ? 'rotate(180deg)' : 'none' }} />}
          sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink.body, textTransform: 'none', '&:hover': { bgcolor: 'transparent', color: tokens.ink.primary } }}
        >
          Have a promo code?
        </Button>
        <Collapse in={promoOpen} orientation="horizontal">
          <TextField
            size="small"
            placeholder="Enter code"
            value={couponCode}
            onChange={e => setCouponCode(e.target.value.toUpperCase())}
            inputProps={{ style: { textTransform: 'uppercase' } }}
            sx={{ width: 180, '& .MuiOutlinedInput-root': { height: 32, borderRadius: '7px', fontSize: 13, bgcolor: tokens.surface.card } }}
          />
        </Collapse>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} sx={{ fontSize: 13.5, fontWeight: 600, color: tokens.ink.bodyAlt, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpgrade}
          disabled={!selectedPlan || selectedPlan === currentPlanId || upgrading}
          endIcon={upgrading ? <CircularProgress size={14} color="inherit" /> : <ArrowForward sx={{ fontSize: 14 }} />}
          sx={{
            height: 38,
            px: 2,
            borderRadius: '9px',
            fontSize: 13.5,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 'none',
            '&.Mui-disabled': { bgcolor: tokens.line.default, color: tokens.ink.faint },
          }}
        >
          {upgrading ? 'Processing…' : 'Confirm Upgrade'}
        </Button>
      </Box>
    </Dialog>
  );
};
